/**
 * Web-side helper pre JS bridge natívnej wrapper appky (Flutter WebView).
 *
 * KANONICKÝ ZDROJ: r-flutter-wrapper/docs/web/app-bridge.js — verziovaný spolu
 * s bridge kontraktom (docs/bridge-contract.md, contract v1). Kópie v eshopoch
 * lokálne neupravovať; zmeny robiť v kanonickom zdroji a rozkopírovať.
 *
 * Bez závislostí a bez build stepu — funguje ako Vite/ESM import aj klasický
 * <script>. Vystavuje window.AppBridge vždy (aj mimo appky — tam je isApp
 * false a volania rejectnú s kódom NOT_IN_APP). Ak existuje window.rEvent
 * (RSHOP), dispatchuje aj "AppBridge.ready" a "AppBridge.<AKCIA>" eventy.
 * Typy: app-bridge.d.ts vedľa tohto súboru.
 *
 * Modul je jediný vlastník window.onFlutterBridgeResult; odpovede sa korelujú
 * len podľa action (kontrakt nemá request ID), preto sa volania tej istej
 * akcie serializujú cez FIFO frontu.
 */
(function () {
    "use strict";

    const isApp = typeof window.FlutterBridge !== "undefined";
    const BRIDGE_VERSION = "1";

    class BridgeError extends Error {
        constructor(code, message) {
            super(message);
            this.name = "BridgeError";
            this.code = code;
        }
    }

    let ready = false;
    let appReadyPayload = null;
    let resolveReady = null;

    const readyPromise = isApp
        ? new Promise((resolve) => {
              resolveReady = resolve;
          })
        : null;

    const pendingByAction = new Map();
    const eventListeners = new Map();

    const isBridgeResult = (result) =>
        typeof result === "object" && result !== null && typeof result.action === "string";

    const dispatchREvent = (name, payload) => {
        if (window.rEvent && typeof window.rEvent.dispatch === "function") {
            window.rEvent.dispatch(name, payload);
        }
    };

    const post = (call) => {
        call.posted = true;

        // token sa číta až pri poste — rotuje každým page loadom, nesmie sa cachovať
        window.FlutterBridge.postMessage(
            JSON.stringify(
                Object.assign(
                    {
                        action: call.action,
                        version: BRIDGE_VERSION,
                        token: window.__flutterBridgeToken,
                    },
                    call.payload !== undefined ? { payload: call.payload } : {}
                )
            )
        );
    };

    const settle = (call, result) => {
        if (call.timer) {
            clearTimeout(call.timer);
        }

        if (call.timedOut) {
            // caller už dostal TIMEOUT reject — neskorú odpoveď len absorbujeme,
            // aby nesadla na ďalšie volanie tej istej akcie
            return;
        }

        if (result.success) {
            call.resolve(result.payload || {});
        } else {
            const error = result.error || {};

            call.reject(
                new BridgeError(
                    error.code || "HANDLER_ERROR",
                    error.message || "Bridge call failed"
                )
            );
        }
    };

    const notifyEventListeners = (event, payload) => {
        const listeners = eventListeners.get(event);

        if (listeners) {
            listeners.forEach((callback) => callback(payload));
        }
    };

    const handleAppReady = (payload) => {
        ready = true;
        appReadyPayload = payload;

        if (resolveReady) {
            resolveReady(payload);
        }

        pendingByAction.forEach((queue) => {
            if (queue.length && !queue[0].posted) {
                post(queue[0]);
            }
        });

        notifyEventListeners("APP_READY", payload);
        dispatchREvent("AppBridge.ready", payload);
    };

    const handleResult = (result) => {
        if (!isBridgeResult(result)) {
            console.warn("[app-bridge] Ignorujem výsledok v neznámom tvare:", result);

            return;
        }

        if (result.action === "APP_READY") {
            handleAppReady(result.payload || {});

            return;
        }

        const queue = pendingByAction.get(result.action);

        if (queue && queue.length && queue[0].posted) {
            const call = queue.shift();

            settle(call, result);

            if (queue.length) {
                post(queue[0]);
            }

            return;
        }

        // nevyžiadaný event (napr. PUSH_TOKEN_READY)
        notifyEventListeners(result.action, result.payload || {});
        dispatchREvent("AppBridge." + result.action, result.payload || {});
    };

    /**
     * Promise resolvne po APP_READY evente aktuálneho page loadu (aj pre neskorých
     * subscriberov). Mimo appky rejectne s BridgeError NOT_IN_APP.
     */
    const whenAppReady = () =>
        readyPromise ||
        Promise.reject(
            new BridgeError("NOT_IN_APP", "FlutterBridge nie je dostupný — stránka nebeží v appke")
        );

    /**
     * Zavolá bridge akciu a resolvne s payloadom odpovede. Volania čakajú na
     * APP_READY a volania tej istej akcie sa serializujú. Bez options.timeoutMs
     * sa nečasuje — akcie s natívnym UI (scanner, biometria, ATT prompt) môžu
     * trvať minúty.
     */
    const callBridge = (action, payload, options) => {
        if (!isApp) {
            return Promise.reject(
                new BridgeError(
                    "NOT_IN_APP",
                    "FlutterBridge nie je dostupný — stránka nebeží v appke"
                )
            );
        }

        const timeoutMs = options && options.timeoutMs;

        return new Promise((resolve, reject) => {
            const call = {
                action,
                payload,
                resolve,
                reject,
                posted: false,
                timedOut: false,
                timer: null,
            };

            if (timeoutMs) {
                call.timer = setTimeout(() => {
                    call.timedOut = true;

                    if (!call.posted) {
                        // ešte sa neodoslalo — odpoveď nepríde, z fronty ho môžeme vyhodiť
                        const queue = pendingByAction.get(action);
                        const index = queue ? queue.indexOf(call) : -1;

                        if (queue && index !== -1) {
                            queue.splice(index, 1);
                        }
                    }

                    reject(
                        new BridgeError(
                            "TIMEOUT",
                            "Akcia " + action + " neodpovedala do " + timeoutMs + " ms"
                        )
                    );
                }, timeoutMs);
            }

            let queue = pendingByAction.get(action);

            if (!queue) {
                queue = [];
                pendingByAction.set(action, queue);
            }

            queue.push(call);

            if (ready && queue[0] === call) {
                post(call);
            }
        });
    };

    /**
     * Prihlási listener na nevyžiadané eventy z appky; vracia unsubscribe funkciu.
     * APP_READY sa neskorým subscriberom prehrá okamžite. Mimo appky no-op.
     */
    const onBridgeEvent = (event, callback) => {
        if (!isApp) {
            return () => undefined;
        }

        if (event === "APP_READY" && appReadyPayload) {
            callback(appReadyPayload);
        }

        let listeners = eventListeners.get(event);

        if (!listeners) {
            listeners = new Set();
            eventListeners.set(event, listeners);
        }

        listeners.add(callback);

        return () => {
            listeners.delete(callback);
        };
    };

    window.AppBridge = { isApp, BridgeError, whenAppReady, callBridge, onBridgeEvent };

    if (isApp) {
        const markBody = () => document.body.classList.add("is-app");

        // pri include v <head> ešte body nemusí existovať
        if (document.body) {
            markBody();
        } else {
            document.addEventListener("DOMContentLoaded", markBody);
        }

        if (typeof window.onFlutterBridgeResult === "function") {
            console.warn(
                "[app-bridge] window.onFlutterBridgeResult už existuje — prepisujem, callback smie vlastniť len tento modul"
            );
        }

        window.onFlutterBridgeResult = handleResult;

        setTimeout(() => {
            if (!ready) {
                console.warn(
                    "[app-bridge] APP_READY neprišiel do 10 s — bridge volania čakajú vo fronte"
                );
            }
        }, 10000);
    }
})();
